import { useEffect, useState } from "react";
import type { Media } from "@/types/media";

type PlayerState = "idle" | "loading" | "unavailable";

interface PlayerDemoProps {
  media: Media;
  episode: number;
}

export default function PlayerDemo({ media, episode }: PlayerDemoProps) {
  const [state, setState] = useState<PlayerState>("idle");

  useEffect(() => {
    setState("idle");
  }, [episode]);

  const handlePlay = () => {
    if (state === "loading") return;
    setState("loading");
    window.setTimeout(() => setState("unavailable"), 1400);
  };

  return (
    <div className="overflow-hidden rounded-lg ring-1 ring-background-200">
      <div className="relative aspect-video w-full bg-primary-950">
        <img
          src={media.cover}
          alt={`${media.title} 播放区演示画面`}
          className="h-full w-full object-top opacity-40"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          {state === "idle" && (
            <>
              <button
                type="button"
                onClick={handlePlay}
                className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-3xl text-primary-950 transition-transform hover:scale-105"
                aria-label="播放演示"
              >
                <i className="ri-play-fill" />
              </button>
              <p className="mt-4 text-sm font-medium text-background-100">
                {media.episodes > 1 ? `第 ${episode} 集 · ` : ""}
                点击播放（演示）
              </p>
              <p className="mt-1 text-xs text-background-300">本区域为界面演示，不提供真实视频播放</p>
            </>
          )}

          {state === "loading" && (
            <>
              <span className="h-12 w-12 animate-spin rounded-full border-4 border-background-50/30 border-t-accent-500" />
              <p className="mt-4 text-sm font-medium text-background-100">正在加载演示画面...</p>
            </>
          )}

          {state === "unavailable" && (
            <>
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50/10 text-3xl text-accent-400">
                <i className="ri-error-warning-line" />
              </span>
              <p className="mt-4 text-sm font-bold text-background-50">暂无播放资源</p>
              <p className="mt-1 max-w-md text-xs leading-relaxed text-background-300">
                这是一个演示页面，播放、下载等接口均为前端示意，不提供真实影视资源。若需观看请前往正规授权平台。
              </p>
              <button
                type="button"
                onClick={() => setState("idle")}
                className="mt-4 rounded-md border border-background-100/40 px-4 py-1.5 text-xs font-semibold text-background-100 transition-colors hover:bg-background-50/10"
              >
                返回
              </button>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 bg-background-50 px-4 py-3">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-movie-2-line text-primary-500" />
          {media.episodes > 1 ? `${media.title} · 第 ${episode} 集` : media.title}
        </span>
        <span className="rounded-md bg-accent-100 px-2 py-1 text-xs font-semibold text-accent-800">
          演示模式 · 无真实播放数据
        </span>
      </div>
    </div>
  );
}